import React, { useEffect, useState } from 'react';
import { Box, Card, Chip } from '@mui/material';
import DataTable from '../../../sharedModules/dataTable';
import { useCollectionEmailSends } from '../../emailHistory/hooks/useCollectionEmailSends';
import {
  CollectionEmailSendListItem,
  CollectionEmailSendStatus,
  SOURCE_LABELS,
  STATUS_LABELS,
} from '../../emailHistory/model';
import { TONE_LABELS } from '../../config/model';
import EmailHistoryDetailModal from '../../emailHistory/components/EmailHistoryDetailModal';

interface Props {
  location: string;
  clientId: number;
}

const statusChipColor = (status: CollectionEmailSendStatus): 'success' | 'error' | 'default' => {
  if (status === 'sent') return 'success';
  if (status === 'failed') return 'error';
  return 'default';
};

const buildColumns = () => [
  {
    name: 'Tone',
    selector: (row: CollectionEmailSendListItem) => row.tone,
    cell: (row: CollectionEmailSendListItem) => <Chip label={TONE_LABELS[row.tone]} size="small" />,
    width: '160px',
  },
  {
    name: 'Status',
    selector: (row: CollectionEmailSendListItem) => row.status,
    cell: (row: CollectionEmailSendListItem) => (
      <Chip label={STATUS_LABELS[row.status]} color={statusChipColor(row.status)} size="small" />
    ),
    width: '110px',
  },
  {
    name: 'Source',
    selector: (row: CollectionEmailSendListItem) => row.send_source,
    cell: (row: CollectionEmailSendListItem) => (
      <Chip label={SOURCE_LABELS[row.send_source]} size="small" variant="outlined" />
    ),
    width: '120px',
  },
  { name: 'Sent At', selector: (row: CollectionEmailSendListItem) => row.delivered_at ?? '—', width: '200px' },
];

const PER_PAGE = 10;

const ClientEmailHistoryTable: React.FC<Props> = ({ location, clientId }) => {
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    setPage(1);
    setSelectedId(null);
  }, [location, clientId]);

  const { data, isLoading, isError } = useCollectionEmailSends({
    location,
    collections_client_id: clientId,
    page,
    per_page: PER_PAGE,
  });

  const rows = data?.data?.result ?? [];
  const totalRecords = data?.data?.total_records ?? 0;

  return (
    <>
      {isError && <Box sx={{ p: 2, color: 'error.main' }}>Failed to load email history.</Box>}
      <Card>
        <DataTable
          keyField="id"
          columns={buildColumns()}
          data={rows}
          progressPending={isLoading}
          pagination
          paginationServer
          paginationPerPage={PER_PAGE}
          paginationRowsPerPageOptions={[PER_PAGE]}
          paginationTotalRows={totalRecords}
          onChangePage={(nextPage: number) => setPage(nextPage)}
          highlightOnHover
          striped
          noHeader
          pointerOnHover
          onRowClicked={(row: CollectionEmailSendListItem) => setSelectedId(row.id)}
          noDataComponent={<Box sx={{ p: 3, color: 'text.secondary' }}>No emails sent to this client yet.</Box>}
        />
      </Card>
      <EmailHistoryDetailModal open={selectedId !== null} emailSendId={selectedId} onClose={() => setSelectedId(null)} />
    </>
  );
};

export default ClientEmailHistoryTable;
